// Keep authenticated traffic on the MIRROR origin. Never make this configurable
// through browser storage: an injected endpoint could otherwise receive the token.
const DEFAULT_API_BASE = '/mirror-assist';
const TOKEN_KEY = 'mirror_auth_token';
const USER_KEY = 'mirror_auth_user';
const QUOTA_KEY = 'mirror_auth_quota';
const FAMILY_KEY = 'mirror_auth_family';
const PARTIES_KEY = 'mirror_auth_parties';
const TIERS_KEY = 'mirror_auth_tiers';
const ACTIVITY_SESSION_KEY = 'mirror_activity_session';
function getApiBase() {
  return DEFAULT_API_BASE;
}

function readJson(key) {
  try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; }
}

export function getAiSession() {
  return {
    token: localStorage.getItem(TOKEN_KEY) || '',
    user: readJson(USER_KEY),
    quota: readJson(QUOTA_KEY),
    family: readJson(FAMILY_KEY),
    parties: readJson(PARTIES_KEY) || [],
    tiers: readJson(TIERS_KEY),
  };
}

export function saveAiSession(payload = {}) {
  const current = getAiSession();
  const next = {
    token: payload.token ?? current.token,
    user: payload.user ?? current.user,
    quota: payload.quota ?? current.quota,
    family: payload.family !== undefined ? payload.family : current.family,
    parties: payload.parties !== undefined ? payload.parties : current.parties,
    tiers: payload.tiers ?? current.tiers,
  };
  if (next.token) localStorage.setItem(TOKEN_KEY, next.token);
  else localStorage.removeItem(TOKEN_KEY);
  if (next.user) localStorage.setItem(USER_KEY, JSON.stringify(next.user));
  else localStorage.removeItem(USER_KEY);
  if (next.quota) localStorage.setItem(QUOTA_KEY, JSON.stringify(next.quota));
  else localStorage.removeItem(QUOTA_KEY);
  if (next.family) localStorage.setItem(FAMILY_KEY, JSON.stringify(next.family));
  else localStorage.removeItem(FAMILY_KEY);
  if (next.parties?.length) localStorage.setItem(PARTIES_KEY, JSON.stringify(next.parties));
  else localStorage.removeItem(PARTIES_KEY);
  if (next.tiers) localStorage.setItem(TIERS_KEY, JSON.stringify(next.tiers));
  else localStorage.removeItem(TIERS_KEY);
  return next;
}

export function clearAiSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(QUOTA_KEY);
  localStorage.removeItem(FAMILY_KEY);
  localStorage.removeItem(PARTIES_KEY);
  localStorage.removeItem(TIERS_KEY);
}

export function lockAiSessionIdentity() {
  // Keep the signed token for an online retry, but never trust a cached user id
  // to choose an IndexedDB profile while the server cannot verify it.
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(QUOTA_KEY);
  localStorage.removeItem(FAMILY_KEY);
  return { ...getAiSession(), token: '', user: null, quota: null, family: null };
}

function retryAfterSeconds(response, payload) {
  const header = String(response.headers.get('retry-after') || '').trim();
  if (/^\d+(?:\.\d+)?$/.test(header)) return Math.max(1, Math.ceil(Number(header)));
  if (header) {
    const date = Date.parse(header);
    if (Number.isFinite(date)) return Math.max(1, Math.ceil((date - Date.now()) / 1000));
  }
  const value = Number(payload?.rateLimit?.retryAfterSeconds ?? payload?.retryAfterSeconds);
  return Number.isFinite(value) && value > 0 ? Math.ceil(value) : null;
}

function cooldownFromResponse(response, payload) {
  const retrySeconds = retryAfterSeconds(response, payload);
  const resetAtValue = payload?.rateLimit?.resetAt || payload?.resetAt || '';
  const parsedReset = Date.parse(resetAtValue);
  const retryAt = Number.isFinite(parsedReset)
    ? parsedReset
    : retrySeconds
      ? Date.now() + retrySeconds * 1000
      : null;
  const code = String(payload?.code || '');
  if (response.status !== 429 && code !== 'monthly_ai_budget_reached') return null;
  return {
    code: code || 'rate_limit_exceeded',
    source: String(payload?.source || payload?.rateLimit?.source || 'mirror'),
    scope: String(payload?.scope || payload?.rateLimit?.scope || 'account'),
    operation: String(payload?.operation || payload?.rateLimit?.operation || 'request'),
    retryAfterSeconds: retrySeconds,
    retryAt,
    resetAt: resetAtValue || (retryAt ? new Date(retryAt).toISOString() : ''),
    limit: Number(payload?.limit || payload?.rateLimit?.limit || 0) || null,
    message: String(payload?.error || ''),
  };
}

function announceCooldown(cooldown) {
  if (!cooldown || typeof window === 'undefined' || typeof CustomEvent === 'undefined') return;
  window.dispatchEvent(new CustomEvent('mirror:cooldown', { detail: cooldown }));
}

export async function mirrorApiFetch(pathname, options = {}, { responseType = 'json' } = {}) {
  const session = getAiSession();
  const headers = new Headers(options.headers || {});
  if (session.token) headers.set('Authorization', `Bearer ${session.token}`);
  if (options.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  const response = await fetch(`${getApiBase()}${pathname}`, {
    ...options,
    headers,
    credentials: 'same-origin',
  });
  const contentType = response.headers.get('content-type') || '';
  const payload = responseType === 'blob' && response.ok
    ? await response.blob()
    : contentType.includes('application/json')
      ? await response.json().catch(() => ({}))
      : await response.text().catch(() => '');
  if (response.status === 401) clearAiSession();
  if (!response.ok) {
    const error = new Error(payload?.error || `MIЯЯOЯ answered ${response.status}.`);
    error.status = response.status;
    error.payload = payload;
    error.code = payload?.code || '';
    error.source = payload?.source || '';
    error.cooldown = cooldownFromResponse(response, payload);
    error.retryAfterSeconds = error.cooldown?.retryAfterSeconds || null;
    error.retryAt = error.cooldown?.retryAt || null;
    announceCooldown(error.cooldown);
    throw error;
  }
  return payload;
}

const apiFetch = mirrorApiFetch;

export async function loginToMirror(username, password, familyCode = '', referralCode = '') {
  const payload = await apiFetch('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password, ...(familyCode.trim() ? { familyCode: familyCode.trim() } : {}), ...(referralCode.trim() ? { referralCode: referralCode.trim() } : {}) }),
  });
  return saveAiSession(payload);
}

export async function createMirrorAccount({ username, displayName, password, accessCode }) {
  const code = String(accessCode || '').trim();
  const payload = await apiFetch('/api/auth/signup', {
    method: 'POST',
    body: JSON.stringify({ username, displayName, password, inviteCode: code, referralCode: code }),
  });
  return saveAiSession(payload);
}

export async function requestPasswordReset(username, note = '') {
  return apiFetch('/api/auth/password-reset-request', {
    method: 'POST',
    body: JSON.stringify({ username, note }),
  });
}

export async function reflectWitnessNote({ note, weather = '', agenda = [] }) {
  const payload = await apiFetch('/api/mirror/witness', {
    method: 'POST',
    body: JSON.stringify({ note, weather, agenda }),
  });
  if (payload.quota) saveAiSession({ quota: payload.quota });
  return payload;
}

export async function refreshMirrorSession() {
  const payload = await apiFetch('/api/auth/me');
  const session = getAiSession();
  return saveAiSession({ ...payload, token: session.token });
}

export async function getCalendarConnections() {
  return apiFetch('/api/integrations/calendar');
}

export async function startGoogleCalendarConnection() {
  return apiFetch('/api/integrations/google/start', { method: 'POST', body: JSON.stringify({}) });
}

export async function syncGoogleCalendar() {
  return apiFetch('/api/integrations/google/sync', { method: 'POST', body: JSON.stringify({}) });
}

export async function disconnectGoogleCalendar() {
  return apiFetch('/api/integrations/google', { method: 'DELETE' });
}

function activitySessionId() {
  try {
    let value = sessionStorage.getItem(ACTIVITY_SESSION_KEY);
    if (!value) {
      value = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(ACTIVITY_SESSION_KEY, value);
    }
    return value;
  } catch {
    return 'browser-session';
  }
}

export async function recordMirrorActivity(view, eventType = 'view') {
  if (!getAiSession().token) return null;
  try {
    return await apiFetch('/api/activity', {
      method: 'POST',
      body: JSON.stringify({
        sessionId: activitySessionId(),
        eventType,
        view: String(view || 'unknown').slice(0, 80),
        path: `${location.pathname}${location.search}`.slice(0, 200),
      }),
    });
  } catch {
    return null;
  }
}

export async function recordDevAttention(report = {}) {
  if (!getAiSession().token) return null;
  try {
    return await apiFetch('/api/dev-attention', {
      method: 'POST',
      body: JSON.stringify({
        route: String(report.route || 'unknown').slice(0, 80),
        message: String(report.message || 'Unknown client failure').slice(0, 1_000),
        stack: String(report.stack || '').slice(0, 8_000),
        componentStack: String(report.componentStack || '').slice(0, 8_000),
        userAgent: String(report.userAgent || '').slice(0, 500),
        viewport: String(report.viewport || '').slice(0, 40),
        build: String(report.build || '').slice(0, 40),
      }),
    });
  } catch {
    return null;
  }
}

export async function getAdminMetrics(days = 30) {
  const safeDays = Math.max(1, Math.min(365, Number(days) || 30));
  return apiFetch(`/api/admin/metrics?days=${encodeURIComponent(safeDays)}`);
}

export async function getAdminDevAttention() {
  return apiFetch('/api/admin/dev-attention');
}

export async function getAdminPasswordResetRequests() {
  return apiFetch('/api/admin/password-reset-requests');
}

export async function getAdminUsers() {
  return apiFetch('/api/admin/users');
}

export async function deleteAdminUser(userId) {
  return apiFetch(`/api/admin/users/${encodeURIComponent(userId)}`, { method: 'DELETE' });
}

export async function createAdminUser({ username, displayName, password, tier }) {
  return apiFetch('/api/admin/users', {
    method: 'POST',
    body: JSON.stringify({ username, displayName, password, tier }),
  });
}

export async function updateAdminUserPlan(userId, { tier }) {
  return apiFetch(`/api/admin/users/${encodeURIComponent(userId)}/plan`, {
    method: 'PATCH',
    body: JSON.stringify({ tier, role: 'user' }),
  });
}

export async function changeMirrorPassword(currentPassword, newPassword) {
  const payload = await apiFetch('/api/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  return saveAiSession(payload);
}

export async function linkMirrorFamily(familyCode) {
  const payload = await apiFetch('/api/family/link', {
    method: 'POST',
    body: JSON.stringify({ familyCode: familyCode.trim() }),
  });
  return saveAiSession({ family: payload.family });
}

export async function getMirrorParties() {
  const payload = await apiFetch('/api/parties');
  return saveAiSession({ parties: payload.parties || [] });
}

export async function createMirrorParty(name) {
  const payload = await apiFetch('/api/parties', { method: 'POST', body: JSON.stringify({ name }) });
  return { ...saveAiSession({ parties: payload.parties || [] }), inviteCode: payload.inviteCode || '' };
}

export async function joinMirrorParty(partyCode) {
  const payload = await apiFetch('/api/parties', { method: 'POST', body: JSON.stringify({ partyCode }) });
  return saveAiSession({ parties: payload.parties || [] });
}

export async function leaveMirrorParty(partyId) {
  const payload = await apiFetch(`/api/parties/${encodeURIComponent(partyId)}`, { method: 'DELETE' });
  return saveAiSession({ parties: payload.parties || [] });
}

export async function getMirrorPartyPosts(partyId, limit = 100) {
  return apiFetch(`/api/parties/${encodeURIComponent(partyId)}/posts?limit=${encodeURIComponent(limit)}`);
}

export async function getMirrorPartyMirror(partyId) {
  return apiFetch(`/api/parties/${encodeURIComponent(partyId)}/mirror`);
}

export async function replaceMirrorPartyMirror(partyId, items = []) {
  return apiFetch(`/api/parties/${encodeURIComponent(partyId)}/mirror`, {
    method: 'PUT',
    body: JSON.stringify({ items }),
  });
}

export async function postMirrorPartyPhoto(partyId, payload) {
  return apiFetch(`/api/parties/${encodeURIComponent(partyId)}/posts`, { method: 'POST', body: JSON.stringify(payload) });
}

export async function fetchMirrorPartyMedia(mediaId, { signal } = {}) {
  if (!mediaId) throw new Error('A party media id is required.');
  return mirrorApiFetch(`/api/party-media/${encodeURIComponent(mediaId)}`, { signal }, { responseType: 'blob' });
}

export async function getMirrorFamilyProfiles() {
  const payload = await apiFetch('/api/family/profiles');
  return saveAiSession({ family: payload.family });
}

export async function selectMirrorFamilyProfile(profileId, password) {
  const payload = await apiFetch('/api/family/select-profile', {
    method: 'POST',
    body: JSON.stringify({ profileId, password }),
  });
  return saveAiSession(payload);
}

export async function uploadMirrorImage(imageData, kind = 'item', { signal } = {}) {
  if (!getAiSession().token) throw new Error('Sign in before syncing photos to MIЯЯOЯ storage.');
  const payload = await apiFetch('/api/mirror/upload-image', {
    method: 'POST',
    body: JSON.stringify({ imageData, kind }),
    signal,
  });
  if (payload.quota) saveAiSession({ quota: payload.quota });
  return payload;
}

export async function fetchMirrorMedia(mediaId, { signal } = {}) {
  if (!mediaId) throw new Error('A private media id is required.');
  return mirrorApiFetch(`/api/media/${encodeURIComponent(mediaId)}`, { signal }, { responseType: 'blob' });
}

export async function scanMirrorItem(item, mode = 'inventory_and_value_scan', { autoScan = false } = {}) {
  if (!getAiSession().token) throw new Error('Sign in to MIЯЯOЯ AI before scanning.');
  const payload = await apiFetch('/api/mirror/ai-fill', {
    method: 'POST',
    body: JSON.stringify({
      ...(item.imageData ? { itemImage: item.imageData } : (!item.imageMediaId && item.careImageData ? { itemImage: item.careImageData } : {})),
      ...(item.imageMediaId ? { itemMediaId: item.imageMediaId } : {}),
      ...(item.backImageData ? { backImage: item.backImageData } : {}),
      ...(item.backImageMediaId ? { backMediaId: item.backImageMediaId } : {}),
      ...(item.tagImageData ? { tagImage: item.tagImageData } : {}),
      ...(item.tagImageMediaId ? { tagMediaId: item.tagImageMediaId } : {}),
      ...(item.careImageData ? { careImage: item.careImageData } : {}),
      ...(item.careImageMediaId ? { careMediaId: item.careImageMediaId } : {}),
      current: {
        label: item.label || '',
        details: item.details || {},
        tags: item.tags || [],
        market: item.aiMarket || {},
        surrounding: item.surrounding || {},
      },
      mode,
      autoScan,
    }),
  });
  if (payload.quota) saveAiSession({ quota: payload.quota });
  return payload;
}

export async function renderMirrorLook({ poseImage = '', poseMediaId = '', garmentImages = [], garmentMediaIds = [], prompt = '', pose = 'front', mode = 'wardrobe', size = '', persistRender = true }) {
  if (!getAiSession().token) throw new Error('Sign in to create a private try-on.');
  const payload = await apiFetch('/api/mirror/ai-render', {
    method: 'POST',
    body: JSON.stringify({
      mode,
      pose,
      prompt,
      persistRender,
      ...(size ? { size } : {}),
      ...(poseImage ? { poseImage } : {}),
      ...(poseMediaId ? { poseMediaId } : {}),
      ...(garmentImages.length ? { garmentImages } : {}),
      ...(garmentMediaIds.length ? { garmentMediaIds } : {}),
    }),
  });
  if (payload.quota) saveAiSession({ quota: payload.quota });
  return payload;
}

export async function renderRoomLayout({ roomImage = '', roomMediaId = '', objectImages = [], objectMediaIds = [], prompt = '', size = '1536x1024' }) {
  return renderMirrorLook({
    poseImage: roomImage,
    poseMediaId: roomMediaId,
    garmentImages: objectImages,
    garmentMediaIds: objectMediaIds,
    prompt,
    pose: 'room',
    mode: 'layout',
    size,
    persistRender: true,
  });
}

export function mergeAiSuggestion(item, suggestion) {
  const details = { ...(item.details || {}) };
  for (const [key, value] of Object.entries(suggestion.details || {})) {
    if (!details[key] && value) details[key] = value;
  }
  const tags = [...new Set([...(item.tags || []), ...(suggestion.tags || [])].map(tag => String(tag).trim()).filter(Boolean))];
  const currentLabel = String(item.label || '').trim();
  const suggestedLabel = String(suggestion.label || '').trim();
  const genericLabel = !currentLabel || /^(object|item|record|untitled(?: object| piece| item)?|new object)$/i.test(currentLabel);
  return {
    label: suggestedLabel && genericLabel ? suggestedLabel : currentLabel || suggestedLabel,
    details,
    tags,
    ocrText: suggestion.rawText || item.ocrText || '',
    ocrUpdatedAt: new Date().toISOString(),
    aiMarket: {
      ...(suggestion.market || {}),
      updatedAt: new Date().toISOString(),
      source: 'AI estimate',
    },
    surrounding: {
      ...(item.surrounding || {}),
      ...(suggestion.surrounding || {}),
    },
    aiConfidence: Number(suggestion.confidence || 0),
    aiNotes: suggestion.notes || '',
    aiNeedsReview: Number(suggestion.confidence || 0) < 80,
    pendingAiSuggestion: Number(suggestion.confidence || 0) < 80 ? suggestion : null,
  };
}

export function marketplaceSearchLinks(item) {
  const query = [item.details?.brand, item.label, item.details?.category, item.details?.color].filter(Boolean).join(' ').trim();
  const encoded = encodeURIComponent(query || item.label || 'clothing');
  return [
    { name: 'eBay', href: `https://www.ebay.com/sch/i.html?_nkw=${encoded}` },
    { name: 'Poshmark', href: `https://poshmark.com/search?query=${encoded}&type=listings&src=dir` },
    { name: 'Depop', href: `https://www.depop.com/search/?q=${encoded}` },
    { name: 'Google', href: `https://www.google.com/search?q=${encodeURIComponent(`${query || item.label} resale`)}` },
  ];
}
